import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightVehicleCapacityDtActions from "../../../components/admin/FreightVehicleCapacityDtActions";
import { AdminDataTable } from "../../../components/datatable";
import { FREIGHT_VEHICLE_CAPACITY_MASTER_COLUMNS } from "../../../config/freightVehicleCapacityAdminColumns";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import { changeVehicleCapacitiesMasterStatus, getVehicleCapacitiesMasterIndex } from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const TABLE_ID = "freight_vehicle_capacities_master_v1";
const COLUMN_DEFS = FREIGHT_VEHICLE_CAPACITY_MASTER_COLUMNS;

const sortRows = (list) =>
  [...list].sort((a, b) => {
    const minDelta = Number(a.min_load) - Number(b.min_load);
    if (minDelta !== 0) return minDelta;
    return Number(a.max_load) - Number(b.max_load);
  });

const FreightVehicleCapacitiesPage = () => {
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Vehicle capacities" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVehicleCapacitiesMasterIndex({ sort_by: "min_load", sort_dir: "asc" });
      const list = res?.data?.vehicleCapacityList ?? res?.data?.list ?? [];
      setRows(sortRows(Array.isArray(list) ? list : []));
    } catch (e) {
      showError(e.message || "Failed to load vehicle capacities");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getNavigate = useCallback(() => navigate, [navigate]);

  const handleToggleStatus = useCallback(
    async (row) => {
      if (!row?.id) return;
      const ok = window.confirm(
        String(row.status) === "1" ? "Deactivate this vehicle capacity?" : "Activate this vehicle capacity?"
      );
      if (!ok) return;
      try {
        const res = await changeVehicleCapacitiesMasterStatus({ id: row.id });
        showSuccess(res?.message || "Status updated");
        await load();
      } catch (e) {
        showError(e.message || "Update failed");
      }
    },
    [load]
  );

  const renderActions = useCallback(
    (row) => (
      <FreightVehicleCapacityDtActions row={row} getNavigate={getNavigate} onToggleStatus={handleToggleStatus} />
    ),
    [getNavigate, handleToggleStatus]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.id, r.min_load, r.max_load, r.status, r.created_at, r.updated_at]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ")
        .includes(q)
    );
  }, [rows, search]);

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by load range, status, id…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
          rightSlot={
            <Link to="/freight/vehicle_capacities/new" className="btn-primary no-underline inline-block">
              + New capacity
            </Link>
          }
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">Vehicle capacities ({filteredRows.length})</h3>
            <button type="button" className="btn-outline text-sm" onClick={() => load()}>
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Define freight vehicle load bands used across profiling and order workflows.
          </p>

          <AdminDataTable
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            rows={filteredRows}
            loading={loading}
            emptyText="No vehicle capacities yet."
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightVehicleCapacitiesPage;
